import React, { Component, ErrorInfo, ReactNode } from 'react';

import { recoverFromRenderError } from '../utils/errorReporter';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * The ErrorBoundary that actually wraps the app (mounted in `App.tsx`, outermost
 * — see that file). There is a second, unused `ErrorBoundary` at
 * `components/error/ErrorBoundary.tsx`: nothing imports it outside its own
 * barrel, and its barrel is imported by nobody either. This is the live one.
 *
 * VRG-427 — the AUTOMATIC half of VRG-378's user-initiated bug reporting: a
 * render error caught here files a report with no user action, then sends the
 * user back to the last route that rendered successfully, where a modal tells
 * them it happened. See `utils/errorReporter.ts` for the reporting + recovery
 * logic and `routeHistory.ts` for how "the previous route" is tracked.
 *
 * SCOPE: this only ever sees RENDER errors — that is the whole contract of a
 * React error boundary. Event-handler throws and unhandled promise rejections
 * never reach `componentDidCatch` and are deliberately NOT covered by this
 * mechanism (see `errorReporter.ts`'s file header for why).
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error);
    console.error('Error stack:', error.stack);
    console.error('Component stack:', errorInfo.componentStack);

    this.setState({
      error,
      errorInfo,
    });

    /*
      AC7 — reporting must NEVER throw back into this boundary. A failure inside
      `recoverFromRenderError` re-entering `componentDidCatch` would be an
      infinite loop that takes the recovery path down with it, so this call is
      wrapped here in addition to the try/catch already inside that function —
      belt and braces around the one failure mode this feature cannot survive.
      `recoverFromRenderError` performs a hard navigation almost immediately
      (see its own comments for why), so the fallback below is only ever on
      screen for a brief instant.
    */
    try {
      recoverFromRenderError(error, errorInfo.componentStack ?? null);
    } catch {
      /* never let a reporting/recovery failure re-enter this boundary */
    }
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div style={{ padding: '20px', color: 'red' }}>
          <h2>Something went wrong.</h2>
          <p>Taking you back&hellip;</p>
        </div>
      );
    }

    return this.props.children;
  }
}
